import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { bibleTestimonials } from '../data/bibleTestimonials';
const AUTOPLAY_MS = 5200;
export function BibleTestimonials() {
  const [[index, direction], setState] = useState<[number, number]>([0, 1]);
  const [paused, setPaused] = useState(false);
  const total = bibleTestimonials.length;
  const go = useCallback((step: number) => {
    setState(([current]) => [(current + step + total) % total, step]);
  }, [total]);
  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => go(1), AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [go, paused]);
  const item = bibleTestimonials[index];
  return <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)} data-id="element-34" data-mp="1rdneawp91997">
      <div className="relative overflow-hidden rounded-2xl border border-[#2c2a30] bg-[linear-gradient(150deg,#1a1820,#111116)] p-6 md:p-10" style={{
      perspective: '1200px'
    }} aria-live="polite" data-id="element-35" data-mp="3i9e48qzps4tx">
        <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-1/3 h-52 w-72 rounded-full bg-[radial-gradient(circle,rgba(233,199,105,0.18),transparent_70%)] blur-2xl" data-id="element-36" data-mp="3r4k65evv62r6" />

        <div className="relative min-h-[220px] md:min-h-[190px]" data-id="element-37" data-mp="33cdgszok2w19">
          <AnimatePresence initial={false} mode="wait" data-id="element-38" data-mp="33epuh0na6k8i">
            <motion.figure key={item.id} initial={{
            opacity: 0,
            x: direction > 0 ? 60 : -60,
            rotateY: direction > 0 ? 8 : -8
          }} animate={{
            opacity: 1,
            x: 0,
            rotateY: 0
          }} exit={{
            opacity: 0,
            x: direction > 0 ? -60 : 60,
            rotateY: direction > 0 ? -8 : 8
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.18} onDragEnd={(_, info) => {
            if (info.offset.x < -60) go(1);
            if (info.offset.x > 60) go(-1);
          }} className="cursor-grab active:cursor-grabbing" data-id="element-39" data-mp="38aolyx3woid5">
              <div aria-label="5 de 5 estrellas" className="flex gap-1 text-[#e9c769]" data-id="element-40" data-mp="3g98d4mrf02rk">
                {[0, 1, 2, 3, 4].map(star => <Star key={star} aria-hidden="true" className="h-4 w-4 fill-[#e9c769]" data-id="element-41" data-mp="3q2gxy9yjuwpb" />)}
              </div>

              <blockquote className="bible-serif-tight mt-5 text-[19px] leading-[1.5] text-[#f7f2e8] md:text-[24px]" data-id="element-42" data-mp="2xiqooudm7tc0">
                “{item.quote}”
              </blockquote>

              <figcaption className="mt-6 flex items-center gap-3" data-id="element-43" data-mp="1824zl24y4cxd">
                <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-[#7b663b] bg-[#141419] text-[12px] font-bold text-[#e9c769]" data-id="element-44" data-mp="1cupp3rgtk3v3">
                  {item.initials}
                </span>
                <span className="text-[13px] leading-tight" data-id="element-45" data-mp="13565gjla85z6">
                  <strong className="block" data-id="element-46" data-mp="1qtpfp4nw6stw">{item.name}</strong>
                  <span className="text-[#beb9b0]" data-id="element-47" data-mp="1qs4mpjh87hy5">{item.role}</span>
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        <div className="mt-7 flex items-center justify-between gap-4" data-id="element-48" data-mp="1ht9e60qplvro">
          <div className="flex gap-2" role="tablist" aria-label="Testimonios" data-id="element-49" data-mp="1lu2xshx4coer">
            {bibleTestimonials.map((testimonial, dotIndex) => <button key={testimonial.id} type="button" role="tab" aria-selected={dotIndex === index} aria-label={`Ver testimonio de ${testimonial.name}`} onClick={() => setState([dotIndex, dotIndex > index ? 1 : -1])} className={`h-1.5 rounded-full transition-all duration-300 ${dotIndex === index ? 'w-7 bg-[#e9c769]' : 'w-1.5 bg-[#4a4750] hover:bg-[#6a6672]'}`} data-id="element-50" data-mp="6gqdkgup9kiw" data-mpi={dotIndex} />)}
          </div>

          <div className="flex gap-2" data-id="element-51" data-mp="14mlk5lsll472">
            <button type="button" onClick={() => go(-1)} aria-label="Testimonio anterior" className="grid h-9 w-9 place-items-center rounded-full border border-[#2c2a30] text-[#beb9b0] transition-colors hover:border-[#7b663b] hover:text-[#e9c769]" data-id="element-52" data-mp="14no9hpa9uykv">
              <ChevronLeft className="h-4 w-4" data-id="element-53" data-mp="3kbobch46zwmu" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Siguiente testimonio" className="grid h-9 w-9 place-items-center rounded-full border border-[#2c2a30] text-[#beb9b0] transition-colors hover:border-[#7b663b] hover:text-[#e9c769]" data-id="element-54" data-mp="3gaunfnqr2hty">
              <ChevronRight className="h-4 w-4" data-id="element-55" data-mp="24syqqxtygcxc" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4 overflow-hidden" data-id="element-56" data-mp="29nmjx9zwlvi9">
        <div className="bible-marquee flex w-max gap-3" data-id="element-57" data-mp="2ejtek6munkkw">
          {[...bibleTestimonials, ...bibleTestimonials].map((testimonial, marqueeIndex) => <span key={`${testimonial.id}-${marqueeIndex}`} className="whitespace-nowrap rounded-full border border-[#2c2a30] bg-[#141419] px-4 py-2 text-[12px] text-[#beb9b0]" data-id="element-58" data-mp="2nljey1ypltut" data-mpi={marqueeIndex}>
                <span className="mr-2 text-[#e9c769]" data-id="element-59" data-mp="t8bf1vvvms2b">★★★★★</span>
                {testimonial.name} · {testimonial.role}
              </span>)}
        </div>
      </div>
    </div>;
}